/**
 * authrefresh.ts -- keep a session alive past the ID token's one-hour life.
 *
 * WHY THIS EXISTS. `refreshTokens()` (auth.ts) worked and was called from exactly ONE place:
 * `acRestoreSession()`, which runs once from `boot()`. So the token was renewed at page load and
 * never again. `idTokenValidity` is 1 hour (auth-stack.ts:100) while the refresh token lasts 30 days,
 * so one hour into any session `authHeader()` began returning `{}` -- it correctly refuses to send an
 * expired token -- every call went out unauthenticated, and the API answered 401. Forever, on every
 * poll tick. Andrei, 2026-08-03: "after some time we start getting 401 Unauthorized error messages
 * and console does not work."
 *
 * WHY A SEPARATE MODULE rather than four lines inside `getJSON`: `getJSON` needs `fetch`, a DOM and
 * module-level `acTokens`, so vitest cannot witness it. The stampede guard and the dead-latch are
 * exactly the logic worth testing, and they are pure. Same reason `apierror.ts` and `auth.ts` are
 * separate.
 *
 * NOT A TOKEN STORE. `isExpired` remains the single source of truth for "is this usable", and
 * `refreshTokens`/`saveTokens`/`clearTokens` do the work. This file owns two facts and nothing else:
 * one refresh in flight at a time, and stop asking once the refresh token is genuinely dead.
 */

import { isExpired, type Tokens } from './auth';

/** What the caller supplies. Injected rather than imported so vitest needs no Cognito. */
export interface RefreshDeps {
  /** Exchange a refresh token for a new session. `null` means it is dead. */
  refresh: (tokens: Tokens) => Promise<Tokens | null>;
  /** Called EXACTLY ONCE when the session cannot be recovered. */
  onDead: () => void;
}

/**
 * ONE refresh in flight, shared by every concurrent caller.
 *
 * Andrei's console shows 4+ simultaneous 401s per tick -- the account snapshot plus three live
 * panes. Without this, each fires its own refresh: a burst of Cognito calls, and a race where a
 * slower response overwrites a newer token.
 */
let inFlight: Promise<Tokens | null> | null = null;

/**
 * Latched once the refresh token is rejected.
 *
 * Without it, a dead 30-day token produces one refresh attempt per request for as long as the tab
 * stays open -- which is the same "wall of errors" failure in a new costume, just with Cognito calls
 * behind it. Cleared by `resetAuthRefresh`, which is also what a fresh sign-in does.
 */
let dead = false;

/**
 * The tokens to use for the next call, refreshing first if the current ones are spent.
 *
 * Returns the SAME object when nothing was needed, so a caller can cheaply tell "unchanged".
 * Returns `null` when there is no usable session -- the caller then sends no Authorization header,
 * exactly as it did before, and an unauthenticated build is unaffected.
 */
export async function ensureFreshTokens(
  tokens: Tokens | null,
  deps: RefreshDeps,
  nowMs = Date.now(),
): Promise<Tokens | null> {
  if (!tokens?.idToken) return null;

  // STILL GOOD: the common path, and it must not touch the network. `isExpired` carries a 30s skew,
  // so this refreshes slightly EARLY rather than mid-request.
  if (!isExpired(tokens.idToken, nowMs)) return tokens;

  if (dead) return null;

  // No refresh token: nothing to exchange, so this session is over now. Do not call Cognito to be
  // told what we can already see.
  if (!tokens.refreshToken) {
    dead = true;
    deps.onDead();
    return null;
  }

  if (!inFlight) {
    inFlight = deps
      .refresh(tokens)
      .then((fresh) => {
        if (!fresh) {
          dead = true;
          deps.onDead();
          return null;
        }
        return fresh;
      })
      .catch(() => {
        // A NETWORK failure is not a dead credential. Do NOT latch: the next call should try again,
        // or a moment of bad wifi would sign the user out for the rest of the tab's life.
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Re-arm after a sign-in (and the reset vitest needs).
 *
 * NOT named for tests:  in the sign-in flow calls this in production. A user who signs in
 * again must not inherit the dead-latch from their previous, expired session. */
export function resetAuthRefresh(): void {
  inFlight = null;
  dead = false;
}
